import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * The Bootstrap modal the Profile Form opens from each section's "Add" button
 * (`#educationModal`, `#workExperienceModal`, `#organizationModal`).
 *
 * Rendered by React rather than driven by `data-bs-toggle`: the modal body is a
 * live form whose state React owns, and letting bootstrap.js move that DOM
 * around is what produced the removeChild crash on the vacancy page. Same
 * classes, so the appearance is unchanged; `show`/`d-block` plus a manual
 * backdrop replace what bootstrap.js would have toggled.
 */
interface ModalProps {
  id: string
  title: string
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
}

export function Modal({ id, title, isOpen, onClose, children, footer }: ModalProps): JSX.Element | null {
  // Bootstrap adds this while a modal is open to stop the page behind scrolling.
  useEffect(() => {
    if (!isOpen) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = previous
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return createPortal(
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <div
        className="modal fade show d-block mt-5"
        id={id}
        tabIndex={-1}
        role="dialog"
        aria-labelledby={`${id}Title`}
        aria-modal="true"
      >
        <div
          className="modal-dialog modal-lg modal-dialog-scrollable"
          role="document"
          style={{ maxHeight: '80vh', marginTop: '10vh' }}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id={`${id}Title`}>
                {title}
              </h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
            </div>
            <div className="modal-body">{children}</div>
            {footer && <div className="modal-footer">{footer}</div>}
          </div>
        </div>
      </div>
    </>,
    document.body,
  )
}
